/* global BigInt */
import React, {useEffect, useState} from 'react';
import {readConfirmedPreorderHistory} from '../../../../../http/orderApi';
import ConfirmedPreorderActions from '../../../../../pages/catalog/oneProductPage/ConfirmedPreorderActions';

const message = 'Не удалось загрузить предзаказ. Попробуйте ещё раз.';
const fail = () => {throw new Error(message);};
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const uuid = value => typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const exact = (value, keys) => value && [Object.prototype, null].includes(Object.getPrototypeOf(value)) &&
  Reflect.ownKeys(value).length === keys.length && keys.every(key => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor && Object.hasOwn(descriptor, 'value') && descriptor.enumerable;
  });
const iso = value => typeof value === 'string' && value.length === 24 && Number.isSafeInteger(Date.parse(value)) &&
  Date.parse(value) >= 0 && new Date(value).toISOString() === value;
const label = value => value === null || typeof value === 'string' && value.length > 0 && value.length <= 510 &&
  value === value.trim() && !/[\u0000-\u001f\u007f-\u009f]/.test(value);
const read = (raw, orderId) => {
  if (exact(raw, ['schemaVersion', 'outcome']) && raw.schemaVersion === 'confirmed-preorder-history-v1' &&
    raw.outcome === 'not_found') return null;
  if (!exact(raw, ['schemaVersion', 'outcome', 'acceptanceKey', 'checkout', 'lines']) ||
    raw.schemaVersion !== 'confirmed-preorder-history-v1' || raw.outcome !== 'accepted' || !uuid(raw.acceptanceKey)) fail();
  const checkout = raw.checkout;
  if (!exact(checkout, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'acceptedAt', 'termsSha256',
    'currency', 'totalAmountMinor', 'paymentMode', 'paymentMethodId', 'paymentState']) ||
    checkout.schemaVersion !== 'confirmed-preorder-checkout-v1' || checkout.outcome !== 'accepted' ||
    checkout.orderId !== orderId || !id(checkout.orderNumber) || !iso(checkout.acceptedAt) ||
    typeof checkout.termsSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(checkout.termsSha256) || checkout.currency !== 'RUB' ||
    typeof checkout.totalAmountMinor !== 'string' || !/^[1-9][0-9]{0,15}$/.test(checkout.totalAmountMinor) ||
    BigInt(checkout.totalAmountMinor) > BigInt(Number.MAX_SAFE_INTEGER) ||
    !['payment_now', 'payment_later'].includes(checkout.paymentMode) || !id(checkout.paymentMethodId) || checkout.paymentState !== 'unpaid') fail();
  if (!Array.isArray(raw.lines) || raw.lines.length < 1 || raw.lines.length > 500 ||
    Reflect.ownKeys(raw.lines).length !== raw.lines.length + 1) fail();
  for (let index = 0; index < raw.lines.length; index++) {
    const descriptor = Object.getOwnPropertyDescriptor(raw.lines, String(index));
    if (!descriptor || !Object.hasOwn(descriptor, 'value') || !descriptor.enumerable) fail();
  }
  const keys = new Set();
  for (const line of raw.lines) {
    if (!exact(line, ['childProductId', 'colorId', 'sizeId', 'sizeName', 'quantity', 'dispatchEta', 'productName', 'colorName', 'article']) ||
      !['childProductId', 'colorId', 'sizeId', 'quantity'].every(key => id(line[key])) || !label(line.sizeName) || line.sizeName === null ||
      ![line.productName, line.colorName, line.article].every(label) || !exact(line.dispatchEta, ['from', 'to']) ||
      !iso(line.dispatchEta.from) || !iso(line.dispatchEta.to) || line.dispatchEta.from > line.dispatchEta.to) fail();
    const key = `${line.childProductId}:${line.colorId}:${line.sizeId}`;
    if (keys.has(key)) fail(); keys.add(key);
  }
  return JSON.parse(JSON.stringify(raw));
};

// Read a saved owner-scoped order; never recreate it from localStorage or
// current catalog prices. Only the existing payment component can start payment.
export default function ConfirmedPreorderHistory({orderId, principalId, onNotFound, customerHistory = false,
  detailsExpanded = false, onToggleDetails}) {
  const [value, setValue] = useState(null), [error, setError] = useState(false), [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true;
    setValue(null); setError(false);
    (async () => {
      try {
        if (!id(orderId) || !id(principalId)) fail();
        const found = read(await readConfirmedPreorderHistory({orderId}), orderId);
        if (!current) return;
        if (found === null) onNotFound(false);
        else setValue(found);
      } catch (_) {if (current) setError(true);}
    })();
    return () => {current = false;};
  }, [orderId, principalId, reload, onNotFound]);
  if (error) return <div aria-live="polite"><p role="alert">{message}</p>
    <button type="button" onClick={() => setReload(value => value + 1)}>Повторить</button></div>;
  if (!value) return <p role="status">Загружаем предзаказ…</p>;
  if (customerHistory) return <section aria-label="Сохранённый предзаказ">
    <div className="user_lc_detail_hidden_zone" hidden={!detailsExpanded}>
      {value.lines.map(line => <div className="user_lc_one_order_item" key={`${line.childProductId}:${line.colorId}:${line.sizeId}`}>
        <div className="user_lc_one_order_content_container">
          <div className="user_lc_one_order_item_name">{line.productName || 'Товар'}</div>
          {line.article && <div className="user_lc_one_order_item_atricul">Артикул: {line.article}</div>}
          <div className="user_lc_one_order_price_counter">Кол-во: {line.quantity}</div>
          {line.colorName && <div className="user_lc_one_order_item_color">Цвет: {line.colorName}</div>}
          <div className="user_lc_one_order_item_size">Размер: {line.sizeName}</div>
          <div className="user_lc_one_order_item_size">Отправим после поступления товара.</div>
        </div>
      </div>)}
      <div className="user_lc_itg_block"><div className="user_lc_itg_block_first_line">
        <span>Итого:</span><span className="price_color">{(Number(value.checkout.totalAmountMinor) / 100).toLocaleString('ru-RU')} ₽</span>
      </div></div>
    </div>
    <button type="button" className="user_lc_button_change_hidden_zone" aria-expanded={detailsExpanded} onClick={onToggleDetails}>
      {detailsExpanded ? 'Свернуть' : 'Подробнее'}
    </button>
    <br/>
    {/* Keep the payment child mounted while order details are expanded or hidden. */}
    <ConfirmedPreorderActions checkout={value.checkout} acceptanceKey={value.acceptanceKey} principalId={principalId}
      customerHistory paymentButtonClassName="user_lc_button_change_hidden_zone"/>
  </section>;
  return <section aria-label="Сохранённый предзаказ">
    <ul>{value.lines.map(line => <li key={`${line.childProductId}:${line.colorId}:${line.sizeId}`}>
      <span>{line.productName || 'Товар'}</span>
      {line.article && <span> — {line.article}</span>}{line.colorName && <span>, цвет: {line.colorName}</span>}
      <p>Размер {line.sizeName}. Количество: {line.quantity}. Отправим после поступления товара.</p>
    </li>)}</ul>
    <p>Сумма предзаказа: {(Number(value.checkout.totalAmountMinor) / 100).toLocaleString('ru-RU')} ₽.</p>
    <ConfirmedPreorderActions checkout={value.checkout} acceptanceKey={value.acceptanceKey} principalId={principalId} customerHistory={customerHistory}/>
  </section>;
}
